import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, CheckCircle2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import FileUploadField from "../components/FileUploadField";
import { api } from "../lib/api";

const initial = {
    artist_name: "",
    email: "",
    song_url: "",
    bio: "",
    genre: "Afrobeats",
    location: "",
    photo_url: "",
    instagram: "",
    twitter: "",
    tiktok: "",
    spotify: "",
};

export default function SubmitPage() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await api.post("/submissions", form);
            setResult(r.data);
            toast.success("Submission received. AI review is ready below.");
            setForm(initial);
        } catch (err) {
            toast.error("Submission failed. Check your details and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-black text-white min-h-screen">
            <SEO
                title="Submit your music"
                description="Upcoming Afrobeats artist? Submit your track to MyStreetzMag and get an instant AI A&R review."
                keywords={["submit music", "Afrobeats artist", "A&R review", "MyStreetzMag"]}
            />
            <Header />
            <main className="max-w-3xl mx-auto px-4 md:px-8 py-12">
                <div className="overline text-streetz-yellow">ARTIST SUBMISSION</div>
                <h1 className="font-display uppercase text-4xl md:text-6xl mt-3 leading-none">
                    Submit your music
                </h1>
                <p className="text-streetz-muted mt-3 max-w-xl">
                    Drop your details. Our AI A&R reviews your sound in seconds, then our editors take it from there.
                </p>

                <form
                    onSubmit={onSubmit}
                    data-testid="artist-submission-form"
                    className="mt-10 bg-streetz-paper border border-streetz-line p-6 md:p-8 flex flex-col gap-4"
                >
                    {[
                        ["artist_name", "Artist / Stage Name *", "text", true],
                        ["email", "Email *", "email", true],
                        ["song_url", "Song link (SoundCloud / Spotify / YouTube) *", "url", true],
                        ["location", "Location (City, Country) *", "text", true],
                    ].map(([name, label, type, required]) => (
                        <label key={name} className="flex flex-col gap-1">
                            <span className="overline text-white">{label}</span>
                            <input
                                type={type}
                                name={name}
                                required={required}
                                value={form[name]}
                                onChange={onChange}
                                data-testid={`submission-${name}-input`}
                                className="bg-black border border-streetz-line px-4 py-3 text-white focus:border-streetz-red outline-none"
                            />
                        </label>
                    ))}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FileUploadField
                            endpoint="/uploads/submission-photo"
                            accept="image/jpeg,image/png,image/webp"
                            label="Artist photo (optional)"
                            testId="submission-photo-upload"
                            onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))}
                        />
                        <FileUploadField
                            endpoint="/uploads/submission-song"
                            accept="audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,audio/aac"
                            label="Song file (optional — overrides song link)"
                            testId="submission-song-upload"
                            onUploaded={(url) => setForm((f) => ({ ...f, song_url: url }))}
                        />
                    </div>

                    <label className="flex flex-col gap-1">
                        <span className="overline text-white">Genre *</span>
                        <select
                            name="genre"
                            value={form.genre}
                            onChange={onChange}
                            data-testid="submission-genre-input"
                            className="bg-black border border-streetz-line px-4 py-3 text-white focus:border-streetz-red outline-none"
                        >
                            {["Afrobeats", "Street Pop", "Hip-Hop", "Amapiano", "R&B", "Highlife", "Other"].map(g => (
                                <option key={g} value={g}>{g}</option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-1">
                        <span className="overline text-white">Bio *</span>
                        <textarea
                            name="bio"
                            required
                            rows={4}
                            value={form.bio}
                            onChange={onChange}
                            data-testid="submission-bio-input"
                            className="bg-black border border-streetz-line px-4 py-3 text-white focus:border-streetz-red outline-none resize-none"
                        />
                    </label>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            ["instagram", "Instagram handle"],
                            ["twitter", "Twitter / X handle"],
                            ["tiktok", "TikTok handle"],
                            ["spotify", "Spotify URL"],
                        ].map(([name, label]) => (
                            <label key={name} className="flex flex-col gap-1">
                                <span className="overline text-white">{label}</span>
                                <input
                                    name={name}
                                    value={form[name]}
                                    onChange={onChange}
                                    data-testid={`submission-${name}-input`}
                                    className="bg-black border border-streetz-line px-4 py-3 text-white focus:border-streetz-red outline-none"
                                />
                            </label>
                        ))}
                    </div>

                    <button
                        disabled={loading}
                        data-testid="submission-submit-button"
                        className="mt-3 bg-streetz-red text-white py-4 overline hover:bg-white hover:text-black transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                    >
                        {loading ? "RUNNING AI REVIEW..." : (<>SUBMIT MUSIC <Sparkles className="w-4 h-4" /></>)}
                    </button>
                </form>

                {result && (
                    <div
                        data-testid="submission-ai-review"
                        className="mt-8 bg-streetz-paper border border-streetz-yellow p-6 md:p-8 flex flex-col gap-4"
                    >
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-streetz-yellow" />
                            <span className="overline text-streetz-yellow">AI A&R REVIEW</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="font-display text-6xl text-white">
                                {result.ai_review?.score ?? "—"}
                                <span className="text-streetz-muted text-2xl">/10</span>
                            </div>
                            <div>
                                <div className="overline text-streetz-red">
                                    {(result.ai_review?.recommendation || "radar").toUpperCase()}
                                </div>
                                <p className="text-white mt-1">{result.ai_review?.verdict}</p>
                            </div>
                        </div>
                        <p className="text-white/85 leading-relaxed border-t border-streetz-line pt-4">
                            {result.ai_review?.profile}
                        </p>
                        <div className="border-t border-streetz-line pt-4">
                            <div className="overline text-streetz-muted mb-1">SUGGESTED CAPTION</div>
                            <p className="text-white">{result.ai_review?.promo_caption}</p>
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}
